import React, { Component, Fragment, ReactNode } from 'react';
import FormicaInput from '../../custom/FormicaInput';
import DataTypeSelectBoxComponent from '../../custom/DataTypeSelectBoxComponent';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
  VariableToken,
} from '@trlogic/ui-pack';
import { dispatch, RootState } from '../../../redux/store';
import { StreamState } from '../../../redux/reducers/StreamReducer';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { changeStream, changeStreamIsUpdate, changeStreamSchema, clearStream } from '../../../redux/actions/StreamActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { StreamColumnJsonModel } from '../../../domain/stream/stream/StreamDomain';
import DataType from '../../../constant/DataType';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../../../constant/CollectionNames';
import VariableType from '../../../constant/VariableType';
import _ from 'lodash';
import { query, repository, subscribe } from '@trlogic/client-js';
import { EnrichmentBase } from '../../../domain/enrichment/EnrichmentDomain';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import RoleHelper from '../../../RoleHelper';
import CustomLoading from '../../custom/CustomLoading';
import { generateUUIDV4, isStringValid } from '../../../util/Generator';
import Icon from '../../../icons/Icon';
import { customConfirm } from 'components/custom/CustomPopupConfirm';
import MessageUtil from '../../../util/MessageUtil';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { openBulkActionDialogFromDependency } from '../../../redux/actions/BulkActionActions';
import ExpressionBridge from '../../../util/ExpressionBridge';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import LanguageUtil from '../../../util/LanguageUtil';
import { DateVariableProperties, VariableProperties } from '../../../domain/datatype/VariablePropertiesDomains';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Computation = subscribe.Computation;
import SubscriptionContext = subscribe.SubscriptionContext;
import VariablePropertiesJsonSerializer from '../../../serializers/VariablePropertiesJsonSerializer';
import GatewayContainer from '../../../config/GatewayContainer';
import GatewayNames from '../../../constant/GatewayNames';
import ComponentName from '../../../constant/ComponentName';
import { ExpressionUtil } from '../../../util/ExpressionUtil';

class StreamColumnEditorComponent extends Component<Props, State> {
  public state = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private uuid = generateUUIDV4();
  private id: string = 'expression_builder_select' + this.props.column.name + this.uuid;
  private computation: Computation | undefined;
  private subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  private readonly variablePropertiesJsonSerializer: VariablePropertiesJsonSerializer = new VariablePropertiesJsonSerializer(
    GatewayContainer.getInstance().get(GatewayNames.STRUCT_CLIENT),
  );
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);

  public render(): ReactNode {
    const { expressionView, loading } = this.state;
    const {
      stream: { dependencies, stream },
    } = this.props;
    const { column, isNew } = this.props;
    const index = stream.columns.indexOf(column);
    const isDisabled =
      !RoleHelper.canEdit('Stream') || (!isNew && this.isStreamDeployedOrScheduled()) || dependencies.some((d) => d.dependentId == column.name);
    if (expressionView == undefined) {
      return null;
    }
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col justify-center w-full gap-y-3 px-1 py-1'} data-testid={`${ComponentName.STREAM}_column_${index}`}>
        <div className={'flex items-center justify-between gap'}>
          <div className={'w-[200px] flex'}>
            <FormicaInput
              placeholder={LanguageUtil.get({ id: `common.input.placeholder.columnName`, defaultMessage: 'Column Name' })}
              value={column.name}
              disabled={isDisabled}
              onChange={this.onChangeName}
              data-testid={`${ComponentName.STREAM}_column_${index}_name_input`}
            />
          </div>
          <div className={'w-[200px] flex gap-x-5'}>
            <DataTypeSelectBoxComponent
              disabled={isDisabled}
              disabledSelect={isDisabled}
              disableSelect={isDisabled}
              onChangeValue={(e: FormicaSelectBoxItem) => this.onChangeDataType(e)}
              properties={column.properties}
              placeholder={LanguageUtil.get({ id: `common.input.placeholder.dataType`, defaultMessage: 'Column Name' })}
              data-testid={`${ComponentName.STREAM}_column_${index}_data_type_select`}
            />
            {(!this.isStreamDeployedOrScheduled() || isNew) && (
              <div
                className={'flex border-f-black-5 cursor-pointer group hover:border-red-600 border px-2 py-2 w-8 h-8 items-center rounded-full justify-center'}
                onClick={(e) => !isDisabled && this.removeColumn(e)}
                data-testid={`${ComponentName.STREAM}_column_${index}_remove_button`}
              >
                <Icon className={'group-hover:text-red-600 '} icon={'multiplier'} size={20} />
              </div>
            )}
          </div>
        </div>
        <div className={'flex flex-col w-full border-[1px] border-[#E0E4E8] rounded-[4px] py-5 bg-main-gray'}>
          <div className='' id={this.id} data-uid={this.uuid} data-testid={`${ComponentName.STREAM}_column_expression_${index}`}>
            {expressionView && (
              <Fragment>
                <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
              </Fragment>
            )}
            <ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onClose} />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const enrichmentSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS);
    const transactionSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    this.computation = subscribe.Tracker.autorun(this.tracker, [...enrichmentSubscriptions, ...transactionSubscriptions]);
    this.build();
  }

  async componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.stream.stream.transaction != this.props.stream.stream.transaction) {
      await this.refreshVariables();
    }
    if (this.props.disabled != prevProps.disabled) {
      const { column, disabled } = this.props;
      const {
        stream: { dependencies },
      } = this.props;
      const isDisabled =
        disabled || !RoleHelper.canEdit('Stream') || this.isStreamDeployedOrScheduled() || dependencies.some((d) => d.dependentId == column.name);
      if (isDisabled) {
        this.state.expressionView?.pause();
      } else {
        this.state.expressionView?.resume();
      }
    }
    if (this.props.streamUpdated != prevProps.streamUpdated) {
      this.state.expressionView?.terminate();
      this.setState({ expressionView: undefined }, () => {
        this.build();
      });
    }
  }

  public componentWillUnmount() {
    const { expressionView } = this.state;
    expressionView?.terminate();
    this.computation?.stop();
  }

  private build = async () => {
    const { column, disabled, isNew } = this.props;
    const {
      stream: { dependencies },
    } = this.props;
    const isDisabled =
      disabled || !RoleHelper.canEdit('Stream') || (!isNew && this.isStreamDeployedOrScheduled()) || dependencies.some((d) => d.dependentId == column.name);
    let expression = this.props.column.expression;
    const variables = await this.prepareVariables();
    await this.expressionBridge.start(expression!, variables);
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: `#${this.id}`,
      model: expression!,
      profile: new ArchiosExpressionProfile(),
      returnType:
        column.properties.dataType == DataType.DATE
          ? { dataType: DataType.NUMBER }
          : await this.variablePropertiesJsonSerializer.deserialize(column.properties),
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as any);
    this.setState({ expressionView, loading: false }, () => {
      expressionView.initialize();
      if (isDisabled) expressionView.pause();
    });
  };

  private tracker = async () => {
    const { expressionView } = this.state;
    if (
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS).some((e) => e.ready()) ||
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS).some((e) => e.ready())
    ) {
      const variables = await this.prepareVariables();
      expressionView?.getContext().setVariables(variables);
    }
  };

  private onChangeSyntax = (updatedExpression: Syntax): void => {
    const {
      column,
      changeStream,
      stream: { stream },
    } = this.props;
    column.expression = updatedExpression;
    changeStream(stream);
  };

  private onClose() {}

  private onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      column,
      changeStream,
      stream: { stream },
    } = this.props;
    if (isStringValid(e.target.value)) {
      column.name = e.target.value.trim();
      changeStream(stream);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDataType = (e: FormicaSelectBoxItem) => {
    const {
      column,
      changeStream,
      stream: { stream },
    } = this.props;
    const { expressionView } = this.state;
    column.properties = e.properties || { dataType: DataType.STRING };
    let properties: VariableProperties = column.properties;
    if (column.properties.dataType == DataType.DATE) {
      (column.properties as DateVariableProperties).format = 'epochMillis';
      properties = {
        dataType: DataType.NUMBER,
      };
    }
    changeStream(stream);
    expressionView?.setReturnType(properties);
  };

  private refreshVariables = async () => {
    const { expressionView } = this.state;
    this.setState({ loading: true });
    const variables = await this.prepareVariables();
    expressionView?.getContext().setVariables(variables);
    this.setState({ loading: false });
  };

  private removeColumn = async (e: any) => {
    const {
      column,
      changeStream,
      stream: { stream, streamSchema, dependencies },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      message: LanguageUtil.get({ id: 'messages.confirm.removeColumn', defaultMessage: 'Are you sure you want to delete this column?' }),
      accept: async () => {
        const dependency = dependencies.filter((d) => d.dependentId == column.name);
        if (dependency.length > 0) {
          dispatch(openBulkActionDialogFromDependency({ id: stream.name, reason: 'DELETE', dependencies: dependency, status: false }, ComponentName.STREAM));
          return;
        }
        if (streamSchema?.stage != 'ACTIVE') {
          const index = stream.columns.findIndex((c) => c.name == column.name);
          stream.columns.splice(index, 1);
          this.props.onUpdate?.();
          changeStream(stream);
          return;
        }
        MessageUtil.error({
          content: LanguageUtil.get({ id: 'messages.error.velocityDeployed', defaultMessage: 'You can not remove column when velocity deployed.' }),
        });
      },
    });
  };

  private isStreamDeployedOrScheduled = () => {
    const {
      stream: { stream },
    } = this.props;
    return stream.stage != 'DRAFT';
  };

  private prepareVariables = async (): Promise<Record<string, TokenVariable[]>> => {
    const {
      stream: { stream },
    } = this.props;
    const event: TransactionJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', stream.transaction)).build(),
    );
    if (event == undefined) return {};
    const eventVariables = event.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE,
        properties: v.properties,
        name: v.name,
        description: v.description,
        args: {
          event: event.name,
        },
      },
    }));
    const enrichmentVariables: Record<
      Exclude<VariableType, VariableType.EVENT_VARIABLE | VariableType.ACTOR_VARIABLE>,
      TokenVariable<VariableToken>[]
    > = _.groupBy(
      ((await this.repository.findMany(query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).build())) as EnrichmentBase[]).map((e) => ({
        type: e.type,
        variable: {
          type: 'Common Variable',
          variableType: e.type,
          name: e.name,
          properties: e.properties,
          description: e.description,
          args: {},
        },
      })),
      (e: any) => e.type,
    ) as Record<any, any[]>;
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
      ...enrichmentVariables,
    };
  };
}

interface Props extends ReduxProps {
  column: StreamColumnJsonModel;
  onUpdate?: () => void;
  disabled: boolean;
  streamUpdated: number;
  isNew: boolean;
}

class State {
  expressionView: ExpressionBuilderView | undefined;
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  stream: state.stream as StreamState,
});

const mapDispatchToProps = { enterComponent, leaveComponent, changeStream, changeStreamIsUpdate, clearStream, changeStreamSchema };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(StreamColumnEditorComponent);
